<template>
  <div
    v-if="facets"
    :class="{
      'facets-menu-fi': true,
      'facets-menu-fi--has-facets': displayedFacets.length,
      'facets-menu-fi--has-chips': selectedElements?.length,
    }"
  >
    <div
      v-if="isSM || showFacetsMobile || notMounted"
      :class="{
        'facets-menu-fi__facets': true,
        'facets-menu-fi__facets--leaving': leavingFacetsMobile,
        'facets-menu-fi__facets--show-result-btn': showMobileResultButton,
        'facets-menu-fi__facets--ssr': notMounted,
        [`facets-menu-fi__facets--cols-${columnsMax}`]: columnsMax,
      }"
    >
      <div
        v-if="!isSM || notMounted"
        class="facets-menu-fi__mobile-filter-header"
      >
        <div
          class="facets-menu-fi__mobile-filter-back"
          data-test="close-mobile-facets"
          @click="closeMobileFacets(false)"
        >
          <div class="facets-menu-fi__mobile-filter-toggle">
            <icon-fi icon="arrow-left" />
            {{ $dict.get('facets.Show-Filters-Mobile') }}
          </div>
        </div>
        <clear-all-facets-fi
          v-if="allowClearAll && selectedElements?.length"
          data-test="clear-all-facets"
          @click="clearAllFacets"
        />
      </div>

      <div
        :style="isSM ? facetsStyle : {}"
        :class="{
          'facets-menu-fi__facets-wrapper': true,
          'facets-menu-fi__facets-wrapper--transitioning': isClosing || isOpening,
        }"
      >
        <div
          ref="selectedFacets"
          :class="{
            'facets-menu-fi__selected-facets': true,
            'facets-menu-fi__selected-facets--fite': isFite,
            'facets-menu-fi__selected-facets--active': showFilters,
            'facets-menu-fi__selected-facets--centered': centered,
            'facets-menu-fi__selected-facets--ssr': notMounted,
          }"
        >
          <div
            v-for="(facet, idx) in displayedFacets"
            :key="facet.name"
            :class="[
              'facets-menu-fi__selected-facet',
              idx === 0 && centered && displayedFacets.length < maxFacetsInRow
                ? `facets-menu-fi__selected-facet--start-${centeredSpace}`
                : null,
            ]"
          >
            <slot
              ref="facets"
              name="facets"
              :facet="facet"
              :open="openFacet === facet.name"
              :toggle-open="toggleOpen"
              :change="change"
              :close="close"
              :idx="idx"
              :register-dom-element="registerDomElement"
            >
              <select-fi
                ref="facets"
                :elements="facet.elements"
                :name="facet.name"
                :multiselect="facet.multiselect"
                :open="openFacet === facet.name"
                large
                @change="change"
                @toggle-open="toggleOpen(facet)"
                @close="close(facet)"
              />
            </slot>
          </div>
        </div>
      </div>

      <button-fi
        v-if="showMobileResultButton && !notMounted"
        type="button"
        class="facets-menu-fi__show-results-mobile button-fi__primary--large"
        data-test="show-results"
        @click="closeMobileFacets(true)"
      >
        {{ $dict.get('search.Show-Results') }}
      </button-fi>
    </div>
    <div
      v-if="(!isSM || notMounted) && displayedFacets?.length"
      class="facets-menu-fi__mobile-filter-toggle-wrapper"
    >
      <div
        class="facets-menu-fi__mobile-filter-toggle"
        data-test="open-mobile-facets"
        @click="openMobileFacets"
      >
        {{ $dict.get('facets.Show-Filters-Mobile') }}
        <icon-fi icon="filter" />
      </div>
    </div>

    <div
      v-if="nonHiddenFacets.length > compactedFacets.length"
      class="facets-menu-fi__filters"
    >
      <div
        v-if="isSM || notMounted"
        class="facets-menu-fi__filters-headline"
        data-test="toggle-facets"
        @click="toggleFacets"
      >
        {{
          showFilters ? $dict.get('facets.Hide-All-Filters') : $dict.get('facets.Show-All-Filters')
        }}
        <span
          :class="{
            'facets-menu-fi__chevron': true,
            'facets-menu-fi__chevron--active': showFilters && !isClosing,
          }"
        >
          <icon-fi icon="chevron-down" />
        </span>
      </div>
    </div>

    <div
      v-if="selectedElements?.length"
      :class="{
        'facets-menu-fi__selected-facets facets-menu-fi__chips': true,
        'facets-menu-fi__selected-facets facets-menu-fi__chips--centered': chipsCentered,
      }"
    >
      <facets-selection-fi
        v-for="selection in selectedElements"
        :key="`${selection.facet.name} ${selection.element.name}`"
        :element="selection.element"
        :facet="selection.facet"
        @change="change"
      >
        <template
          v-if="$slots.selection"
          #display="{ facet, element }"
        >
          <slot
            name="selection"
            :facet="facet"
            :element="element"
          />
        </template>
      </facets-selection-fi>
      <div
        v-if="allowClearAll && selectedElements.length && isSM && showClearAll"
        class="facets-menu-fi__clear-all-btn-wrapper"
      >
        <clear-all-facets-fi
          data-test="clear-all-facets"
          @click="clearAllFacets"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ButtonFi, IconFi, SelectFi } from 'atoms';
import { mapGetters } from 'vuex';

import ClearAllFacetsFi from '@/components/facets/clear-all-facets-fi/ClearAllFacetsFi.vue';
import FacetsSelectionFi from '@/components/facets/facets-selection-fi/FacetsSelectionFi.vue';
import bodyScrollMixin from '@/mixins/bodyScrollMixin.vue';
import breakpointMixin from '@/mixins/breakpointMixin.vue';

export default {
  name: 'FacetsMenuFi',
  components: {
    ButtonFi,
    ClearAllFacetsFi,
    FacetsSelectionFi,
    IconFi,
    SelectFi,
  },
  mixins: [breakpointMixin, bodyScrollMixin],
  props: {
    facets: {
      type: Array,
      required: true,
    },
    allowClearAll: {
      type: Boolean,
      default: true,
    },
    centered: {
      type: Boolean,
      default: false,
    },
    chipsCentered: {
      type: Boolean,
      default: false,
    },
    showClearAll: {
      type: Boolean,
      default: true,
    },
    columnsMax: {
      type: Number,
      default: 6,
    },
  },
  emits: ['change', 'clear-all-facets', 'mobile-facet-closed'],
  data() {
    return {
      notMounted: true,
      showFacetsMobile: false,
      leavingFacetsMobile: false,

      openFacet: null,
      showFilters: false,
      isClosing: false,
      isOpening: false,

      facetsStyle: {},
    };
  },
  computed: {
    ...mapGetters({
      windowDimensions: 'layout/getWindowDimensions',
    }),
    maxFacetsInRow() {
      if (this.isXL) {
        return this.columnsMax;
      }

      if (this.isLG) {
        return 4;
      }

      return 6;
    },
    nonHiddenFacets() {
      return this.facets.filter((f) => !f.noMenu);
    },
    compactedFacets() {
      return this.nonHiddenFacets.slice(0, this.maxFacetsInRow);
    },
    displayedFacets() {
      return this.showFilters || !this.isSM ? this.nonHiddenFacets : this.compactedFacets;
    },
    selectedElements() {
      const selectedElements = [];
      this.facets.forEach((facet) => {
        facet.elements.forEach((element) => {
          if (element.selected) {
            selectedElements.push({
              facet,
              element,
            });
          }
        });
      });
      return selectedElements;
    },
    showMobileResultButton() {
      return !this.isSM && this.selectedElements?.length;
    },
    centeredSpace() {
      const gridCols = 12;
      const itemsCols = (gridCols / this.columnsMax) * this.displayedFacets.length;

      return (gridCols - itemsCols) / 2 + 1;
    },
  },
  watch: {
    'facets.length': async function facetCountChanged() {
      if (this.showFilters) {
        await this.$nextTick();
        this.setFacetsMaxHeight();
      }
    },
    windowDimensions: {
      deep: true,
      handler() {
        this.resize();
      },
    },
  },
  mounted() {
    this.notMounted = false;
  },
  methods: {
    registerDomElement(element) {
      this.collapseToFacet = element;
    },
    clearAllFacets() {
      this.$emit('clear-all-facets');
    },
    openMobileFacets() {
      this.bodyDisableScroll();
      this.showFacetsMobile = true;
    },
    async closeMobileFacets(sendEmit) {
      this.leavingFacetsMobile = true;
      await new Promise((resolve) => {
        setTimeout(resolve, 300);
      });
      this.bodyEnableScroll();
      this.showFacetsMobile = false;
      this.leavingFacetsMobile = false;
      if (sendEmit) {
        this.$emit('mobile-facet-closed');
      }
    },
    setFacetsMaxHeight() {
      if (this.notMounted) {
        return;
      }
      const { height } = this.$refs.selectedFacets.getBoundingClientRect();
      this.facetsStyle = {
        'max-height': `${height}px`,
      };
    },
    async toggleFacets() {
      if (!this.$refs?.facets && !this.collapseToFacet) {
        this.showFilters = !this.showFilters;
        return;
      }

      const getFirstRowHeightStyle = () => {
        const element = this.collapseToFacet || this.$refs.facets[0].$el;
        const { height: elementHeight } = element.getBoundingClientRect();
        const style = element.currentStyle || window.getComputedStyle(element);
        const height = elementHeight + parseFloat(style.marginBottom);
        return {
          'max-height': `${height}px`,
        };
      };

      this.togglingFrom = this.showFilters;
      if (this.showFilters) {
        this.setFacetsMaxHeight();
        this.isClosing = true;
        await this.$nextTick();

        this.facetsStyle = getFirstRowHeightStyle();
        await new Promise((resolve) => {
          setTimeout(resolve, 300);
        });

        this.showFilters = false;
        this.isClosing = false;
        this.facetsStyle = {};
      } else {
        this.facetsStyle = getFirstRowHeightStyle();
        this.showFilters = true;
        this.isOpening = true;
        if (this.$refs.selectedFacets) {
          await this.$nextTick();
          this.setFacetsMaxHeight();
          await new Promise((resolve) => {
            setTimeout(resolve, 300);
          });
        }
        this.isOpening = false;
      }
    },
    toggleOpen(facet) {
      if (this.openFacet === facet.name) {
        this.openFacet = null;
      } else {
        this.openFacet = facet.name;
      }
    },
    close(facet) {
      if (this.openFacet === facet.name) {
        this.openFacet = null;
      }
    },
    change(...params) {
      this.$emit('change', ...params);
    },
    resize() {
      if (this.isSM) {
        this.bodyEnableScroll();
      }
    },
  },
};
</script>

<style scoped src="./facets-menu-fi" />

<style src="./facets-menu-fi-unscoped" />
